/**
 * The web build is retired: mobile.taskshealth.com sends nurses to the store
 * instead of running the app.
 *
 * Why: the browser build never registers a push token (push-notifications.tsx
 * bails on web), so a nurse using the PWA silently receives no visit offers,
 * no compliance reminders, and no chat notifications. She looks idle to
 * coordination while believing she is signed up. Until 2026-08-12 the nurse
 * landing site made the PWA the primary call to action on its apply success
 * screen, which is how a large share of the onboarding cohort ended up here.
 *
 * This module holds the policy. `components/web-retired/WebRetiredGate.tsx`
 * renders it.
 */
import { Platform } from "react-native";

/** Store listings. Same URLs the nurse landing site uses. */
export const APP_STORE_URL = "https://apps.apple.com/us/app/task-health-app/id6760780019";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.taskhealth.app";

/**
 * localStorage key holding the staff bypass.
 *
 * Persisted rather than held in memory so a bypass survives reloads and the
 * in-app navigations that follow. Staff hit this constantly: the admin/tester
 * login backdoor (tap logo 5x, staff password, code 456789) exists precisely so
 * ops can drive the app as a specific nurse in a browser, and the
 * `rn-mobile-login` and `rn-compliance-shots` Playwright skills automate that.
 *
 * Automation should seed this key directly rather than replaying taps:
 *
 *     await page.addInitScript(() => {
 *         localStorage.setItem("th-web-retired-bypass", "1");
 *     });
 */
export const WEB_RETIRED_BYPASS_KEY = "th-web-retired-bypass";

/** Taps on the wordmark that reveal the bypass. Matches the login backdoor. */
export const BYPASS_TAP_THRESHOLD = 5;

/** Taps must land inside this window, so stray taps never accumulate. */
export const BYPASS_TAP_WINDOW_MS = 3000;

/**
 * Whether this build is subject to the block at all.
 *
 * Native is never blocked — it IS the destination. Local development is never
 * blocked either, because `pnpm --filter mobile dev` serves the app on web and
 * blocking it would make the fastest development loop in the repo unusable.
 * `__DEV__` is false for every `expo export` build, so staging and production
 * both enforce it.
 */
export function isWebRetiredEnforced(): boolean {
    return Platform.OS === "web" && !__DEV__;
}

/** True when staff have already tapped through on this browser. */
export function hasStoredBypass(): boolean {
    if (Platform.OS !== "web") {
        return false;
    }
    try {
        return globalThis.localStorage?.getItem(WEB_RETIRED_BYPASS_KEY) === "1";
    } catch {
        // Safari private mode throws on localStorage access. Treat an
        // unreadable store as "no bypass" rather than crashing the gate: a
        // nurse who cannot be read is a nurse who should see the block.
        return false;
    }
}

/** Records the bypass so it survives a reload. */
export function storeBypass(): void {
    if (Platform.OS !== "web") {
        return;
    }
    try {
        globalThis.localStorage?.setItem(WEB_RETIRED_BYPASS_KEY, "1");
    } catch {
        // Non-fatal: the bypass still applies for this page load, it just will
        // not survive a refresh.
    }
}

/**
 * Which store to lead with, from the browser UA.
 *
 * Returns null when we cannot tell, and the screen then shows both buttons.
 * iPadOS 13+ reports a desktop Safari UA, so iPads land in that both-buttons
 * case, which is the right failure: it shows a real choice rather than guessing
 * wrong.
 */
export function detectStoreUrl(): string | null {
    if (Platform.OS !== "web") {
        return null;
    }
    const ua = globalThis.navigator?.userAgent?.toLowerCase() ?? "";
    // Android first: some Android browsers carry "like Mac OS X" in the UA.
    if (ua.includes("android")) {
        return PLAY_STORE_URL;
    }
    if (/iphone|ipad|ipod/.test(ua)) {
        return APP_STORE_URL;
    }
    return null;
}
